<script setup lang="ts">
/**
 * Inline SVG smiley face icon drawn in the app's success color
 * (`--color-tufte-success`). Used on the WelcomeTab logged-in state and
 * anywhere a "success" illustration is needed.
 *
 * Props
 *   size — pixel number (applied to `width`/`height` attributes) or any CSS
 *          value string such as `'8rem'` or `'20vmin'` (applied via `style`).
 *          Defaults to `80` (px).
 */
const props = withDefaults(
  defineProps<{
    /** Size: number (pixels) or string (e.g. '8rem', '20vmin') for responsive sizing. */
    size?: number | string
  }>(),
  { size: 80 },
)

const isPixelSize = typeof props.size === 'number'
const sizeStyle = isPixelSize ? undefined : { width: props.size, height: props.size }
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    :width="isPixelSize ? size : undefined"
    :height="isPixelSize ? size : undefined"
    :style="sizeStyle"
    aria-hidden="true"
    class="happy-smile-icon"
  >
    <!-- Face circle: transparent fill, success stroke -->
    <circle
      cx="50"
      cy="50"
      r="45"
      fill="transparent"
      stroke="var(--color-tufte-success)"
      stroke-width="2.5"
    />
    <!-- Happy eyes (small upward arcs) -->
    <path
      d="M 30 42 Q 35 36 40 42"
      fill="none"
      stroke="var(--color-tufte-success)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <path
      d="M 60 42 Q 65 36 70 42"
      fill="none"
      stroke="var(--color-tufte-success)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <!-- Happy mouth (smile) -->
    <path
      d="M 32 58 Q 50 72 68 58"
      fill="none"
      stroke="var(--color-tufte-success)"
      stroke-width="2.5"
      stroke-linecap="round"
    />
  </svg>
</template>

<style scoped>
.happy-smile-icon {
  display: block;
}
</style>
